import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Pencil, Mail, Phone, MapPin, Calendar, RefreshCw, CreditCard, AlertTriangle, UserPlus, Globe, Eye, EyeOff, Copy, Check, KeyRound, FileText, ExternalLink, Send } from "lucide-react";
import api from "@/lib/api";
import CustomerNotes from "./CustomerNotes";

const statusBadge = { active: "crm-badge crm-badge-green", inactive: "crm-badge crm-badge-gray", blocked: "crm-badge crm-badge-red" };
const statusLabels = { active: "Ativo", inactive: "Inativo", blocked: "Bloqueado" };
const subStatusBadge = { active: "crm-badge crm-badge-green", past_due: "crm-badge crm-badge-red", cancelled: "crm-badge crm-badge-gray" };
const subStatusLabels = { active: "Ativa", past_due: "Vencida", cancelled: "Cancelada" };
const payStatusBadge = { pending: "crm-badge crm-badge-orange", paid: "crm-badge crm-badge-green", failed: "crm-badge crm-badge-red", refunded: "crm-badge crm-badge-gray" };
const payStatusLabels = { pending: "Pendente", paid: "Pago", failed: "Falhou", refunded: "Reembolsado" };
const portalStatusLabels = { invited: "Convidado", active: "Ativo" };

function PortalAccessCard({ customer, onInvite, inviting, inviteLink, emailSent }) {
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(null);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  if (!customer.portal_enabled) {
    return (
      <div className="crm-card p-6 mb-8" style={{ border: "2px dashed var(--crm-border)" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "var(--crm-input-bg)", color: "var(--crm-text-dim)" }}>
              <Globe className="w-7 h-7" />
            </div>
            <div>
              <p className="text-base font-bold" style={{ color: "var(--crm-text)" }}>Portal do Cliente</p>
              <p className="text-sm" style={{ color: "var(--crm-text-dim)" }}>Acesso ainda não habilitado</p>
            </div>
          </div>
          <button onClick={onInvite} disabled={inviting} data-testid="invite-portal-btn" className="crm-btn-primary">
            <UserPlus className="w-4 h-4" /> {inviting ? "Enviando..." : "Convidar para Portal"}
          </button>
        </div>
      </div>
    );
  }

  const isActive = customer.portal_access_status === "active";

  return (
    <div className="crm-card overflow-hidden mb-8" data-testid="portal-access-card"
      style={{ border: `2px solid ${isActive ? "rgba(16,185,129,0.4)" : "rgba(249,115,22,0.4)"}` }}>
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between"
        style={{ background: isActive ? "rgba(16,185,129,0.06)" : "rgba(249,115,22,0.06)", borderBottom: "1px solid var(--crm-border)" }}>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{ background: isActive ? "rgba(16,185,129,0.15)" : "rgba(249,115,22,0.15)", color: isActive ? "#10b981" : "#f97316" }}>
            <Globe className="w-7 h-7" />
          </div>
          <div>
            <p className="text-lg font-bold" style={{ color: "var(--crm-text)" }}>Portal do Cliente</p>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold mt-1"
              style={{
                background: isActive ? "rgba(16,185,129,0.12)" : "rgba(249,115,22,0.12)",
                color: isActive ? "#10b981" : "#f97316",
                border: `1.5px solid ${isActive ? "rgba(16,185,129,0.3)" : "rgba(249,115,22,0.3)"}`,
              }}>
              {isActive ? <Check className="w-3.5 h-3.5" /> : <KeyRound className="w-3.5 h-3.5" />}
              {isActive ? "ATIVO" : "CONVIDADO — Aguardando ativação"}
            </span>
          </div>
        </div>
        {!isActive && (
          <button onClick={onInvite} disabled={inviting} className="crm-btn-secondary text-sm">
            <UserPlus className="w-4 h-4" /> Reenviar Convite
          </button>
        )}
      </div>

      {/* Credentials */}
      <div className="px-6 py-5 space-y-4">
        {/* Email */}
        <div className="flex items-center justify-between py-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--crm-text-muted)" }}>Email de Acesso</p>
            <p className="text-base font-medium mt-1" style={{ color: "var(--crm-text)" }}>{customer.email}</p>
          </div>
          <button onClick={() => copyToClipboard(customer.email, "email")}
            className="p-2 rounded-lg transition-colors" style={{ color: copied === "email" ? "#10b981" : "var(--crm-text-dim)" }}>
            {copied === "email" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>

        {/* Password */}
        {customer.portal_password_plain && (
          <div className="flex items-center justify-between py-2" style={{ borderTop: "1px solid var(--crm-border)" }}>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--crm-text-muted)" }}>Senha do Portal</p>
              <p className="text-base font-mono font-medium mt-1" style={{ color: "var(--crm-text)" }}>
                {showPassword ? customer.portal_password_plain : "••••••••"}
              </p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setShowPassword(!showPassword)}
                className="p-2 rounded-lg transition-colors" style={{ color: "var(--crm-text-dim)" }}>
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button onClick={() => copyToClipboard(customer.portal_password_plain, "password")}
                className="p-2 rounded-lg transition-colors" style={{ color: copied === "password" ? "#10b981" : "var(--crm-text-dim)" }}>
                {copied === "password" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}

        {/* Invite link */}
        {inviteLink && (
          <div className="py-3 px-4 rounded-xl" style={{ borderTop: "1px solid var(--crm-border)", background: "rgba(16,185,129,0.04)" }}>
            <div className="flex items-center gap-2 mb-2">
              <p className="text-xs font-semibold" style={{ color: "#10b981" }}>Link de Convite</p>
              {emailSent === true && <span className="text-[10px] font-bold px-2 py-0.5 rounded-md" style={{ background: "rgba(16,185,129,0.1)", color: "#10b981" }}>Email enviado</span>}
              {emailSent === false && <span className="text-[10px] font-bold px-2 py-0.5 rounded-md" style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444" }}>Email não enviado</span>}
            </div>
            <div className="flex items-center gap-2">
              <p className="text-xs break-all flex-1" style={{ color: "var(--crm-text-secondary)" }}>{inviteLink}</p>
              <button onClick={() => copyToClipboard(inviteLink, "link")}
                className="p-1.5 rounded-lg flex-shrink-0 transition-colors" style={{ color: copied === "link" ? "#10b981" : "var(--crm-text-dim)" }}>
                {copied === "link" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [subs, setSubs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [inviteLink, setInviteLink] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [emailSent, setEmailSent] = useState(null);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    api.get(`/customers/${id}`).then((r) => setCustomer(r.data)).catch(() => navigate("/crm/customers"));
    api.get(`/customers/${id}/subscriptions`).then((r) => setSubs(r.data)).catch(() => {});
    api.get(`/customers/${id}/payments`).then((r) => setPayments(r.data)).catch(() => {});
    api.get(`/customers/${id}/invoices`).then((r) => setInvoices(r.data)).catch(() => {});
  }, [id]);

  if (!customer) return <div className="animate-pulse"><div className="h-8 rounded w-48 mb-6" style={{ background: "var(--crm-border)" }} /><div className="h-64 crm-card" /></div>;

  const addr = customer.address || {};
  const fullAddress = [addr.zipcode, addr.prefecture, addr.city, addr.address1, addr.address2].filter(Boolean).join(", ");
  const today = new Date().toISOString().slice(0, 10);

  const handleInvitePortal = async () => {
    setInviting(true);
    try {
      const { data } = await api.post(`/customers/${id}/invite-portal`);
      setInviteLink(data.invite_link);
      setEmailSent(data.email_sent);
      api.get(`/customers/${id}`).then((r) => setCustomer(r.data));
    } catch { /* */ }
    setInviting(false);
  };

  const InfoRow = ({ icon: Icon, value }) => (
    <div className="flex items-center gap-3 py-2">
      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: "var(--crm-text-dim)" }} />
      <span className="text-sm" style={{ color: "var(--crm-text-secondary)" }}>{value}</span>
    </div>
  );

  const AddrRow = ({ label, value }) => (
    <div className="flex justify-between py-1.5">
      <span className="text-sm" style={{ color: "var(--crm-text-muted)" }}>{label}</span>
      <span className="text-sm font-medium" style={{ color: "var(--crm-text)" }}>{value || "—"}</span>
    </div>
  );

  return (
    <div data-testid="customer-detail-page">
      <button onClick={() => navigate("/crm/customers")} className="inline-flex items-center gap-2 text-sm mb-6 transition-colors" style={{ color: "var(--crm-text-muted)" }}>
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="crm-heading">{customer.name}</h1>
          <span className={`mt-2 inline-block ${statusBadge[customer.status]}`}>{statusLabels[customer.status]}</span>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Link to={`/crm/customers/${id}/edit`} data-testid="edit-customer-btn" className="crm-btn-secondary"><Pencil className="w-4 h-4" /> Editar</Link>
        </div>
      </div>

      {/* Portal Access Card */}
      <PortalAccessCard customer={customer} onInvite={handleInvitePortal} inviting={inviting} inviteLink={inviteLink} emailSent={emailSent} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="crm-card p-6">
          <h2 className="crm-subheading">Informações de Contato</h2>
          <div className="space-y-1">
            <InfoRow icon={Mail} value={customer.email} />
            <InfoRow icon={Phone} value={customer.phone || "—"} />
            <InfoRow icon={MapPin} value={fullAddress || "Endereço não informado"} />
            <InfoRow icon={Calendar} value={`Cadastrado em ${new Date(customer.created_at).toLocaleDateString("pt-BR")}`} />
          </div>
        </div>
        <div className="crm-card p-6">
          <h2 className="crm-subheading">Endereço Completo</h2>
          <div className="space-y-0.5">
            <AddrRow label="CEP" value={addr.zipcode} />
            <AddrRow label="Prefeitura" value={addr.prefecture} />
            <AddrRow label="Cidade" value={addr.city} />
            <AddrRow label="Endereço 1" value={addr.address1} />
            <AddrRow label="Endereço 2" value={addr.address2} />
          </div>
          {(addr.prefecture_romaji || addr.city_romaji || addr.address1_romaji) && (
            <>
              <div className="my-4" style={{ borderTop: "1px solid var(--crm-border)" }} />
              <h2 className="crm-subheading">Romaji</h2>
              <div className="space-y-0.5">
                <AddrRow label="Prefeitura" value={addr.prefecture_romaji} />
                <AddrRow label="Cidade" value={addr.city_romaji} />
                <AddrRow label="Endereço 1" value={addr.address1_romaji} />
                <AddrRow label="Endereço 2" value={addr.address2_romaji} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Subscriptions */}
      <div className="crm-card overflow-hidden mb-6">
        <div className="px-6 py-4 flex items-center gap-2" style={{ borderBottom: "1px solid var(--crm-border)" }}>
          <RefreshCw className="w-4 h-4" style={{ color: "var(--crm-accent)" }} />
          <h2 className="text-sm font-bold" style={{ color: "var(--crm-text)" }}>Assinaturas ({subs.length})</h2>
        </div>
        {subs.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm" style={{ color: "var(--crm-text-dim)" }}>Nenhuma assinatura</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="crm-table">
              <thead><tr><th>Plano</th><th>Valor</th><th>Próx. Cobrança</th><th>Status</th></tr></thead>
              <tbody>
                {subs.map((s) => {
                  const isOverdue = s.status === "active" && s.next_billing_date && s.next_billing_date < today;
                  return (
                    <tr key={s.id}>
                      <td className="primary">{s.product_name}</td>
                      <td style={{ fontWeight: 600 }}>¥{s.monthly_amount.toLocaleString()}/mês</td>
                      <td>
                        <span style={{ color: isOverdue ? "#ef4444" : undefined }} className="flex items-center gap-1">
                          {isOverdue && <AlertTriangle className="w-3.5 h-3.5" />}
                          {s.next_billing_date || "—"}
                        </span>
                      </td>
                      <td><span className={subStatusBadge[isOverdue ? "past_due" : s.status]}>{subStatusLabels[isOverdue ? "past_due" : s.status]}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payments */}
      <div className="crm-card overflow-hidden">
        <div className="px-6 py-4 flex items-center gap-2" style={{ borderBottom: "1px solid var(--crm-border)" }}>
          <CreditCard className="w-4 h-4" style={{ color: "var(--crm-accent)" }} />
          <h2 className="text-sm font-bold" style={{ color: "var(--crm-text)" }}>Histórico de Pagamentos ({payments.length})</h2>
        </div>
        {payments.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm" style={{ color: "var(--crm-text-dim)" }}>Nenhum pagamento</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="crm-table">
              <thead><tr><th>Valor</th><th>Vencimento</th><th>Pagamento</th><th>Status</th></tr></thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>¥{p.amount.toLocaleString()}</td>
                    <td>{p.due_date}</td>
                    <td>{p.payment_date || "—"}</td>
                    <td><span className={payStatusBadge[p.status]}>{payStatusLabels[p.status]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Stripe Invoices */}
      {invoices.length > 0 && (
        <div className="crm-card overflow-hidden mt-6">
          <div className="px-6 py-4 flex items-center gap-2" style={{ borderBottom: "1px solid var(--crm-border)" }}>
            <FileText className="w-4 h-4" style={{ color: "var(--crm-accent)" }} />
            <h2 className="text-sm font-bold" style={{ color: "var(--crm-text)" }}>Faturas Stripe ({invoices.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="crm-table">
              <thead><tr><th>Data</th><th>Valor</th><th>Vencimento</th><th>Status</th><th className="text-right">Ações</th></tr></thead>
              <tbody>
                {invoices.map((inv) => {
                  const invStatus = inv.status === "paid" ? { badge: "crm-badge crm-badge-green", label: "Pago" } : inv.status === "open" ? { badge: "crm-badge crm-badge-orange", label: "Aberta" } : { badge: "crm-badge crm-badge-red", label: inv.status };
                  return (
                    <tr key={inv.id}>
                      <td>{inv.created}</td>
                      <td style={{ fontWeight: 700 }}>¥{inv.amount?.toLocaleString()}</td>
                      <td>{inv.due_date || "—"}</td>
                      <td><span className={invStatus.badge}>{invStatus.label}</span></td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {inv.hosted_invoice_url && (
                            <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                              style={{ color: "var(--crm-accent)", background: "var(--crm-accent-subtle)" }}>
                              <ExternalLink className="w-3 h-3" /> Ver Fatura
                            </a>
                          )}
                          {inv.status === "open" && (
                            <button onClick={async () => { await api.post(`/invoices/${inv.id}/resend`); alert("Fatura reenviada!"); }}
                              className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                              style={{ color: "#3b82f6", background: "rgba(59,130,246,0.08)" }}>
                              <Send className="w-3 h-3" /> Reenviar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Notes */}
      <div className="mt-6">
        <CustomerNotes customerId={id} />
      </div>
    </div>
  );
}
